import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  Typography,
  Chip,
  InputAdornment,
  CircularProgress,
  Pagination,
  Grid,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
  Container,
  Avatar,
  Paper,
  TableSortLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Toolbar
} from '@mui/material';
import {
  Search,
  Add,
  Visibility,
  Edit,
  Delete,
  Phone,
  Email,
  FilterList,
  Business,
  Person,
  ContactPhone,
  MoreVert,
  StarBorder,
  Star,
  CallMade,
  EmailOutlined,
  PhoneOutlined
} from '@mui/icons-material';
import { getContacts, getContactsViaQuickSearch } from '../../service/Contacts/getContacts';
import { useDebouncedCallback } from 'use-debounce';
import {OpScore} from './contactScore';

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Calculate pagination values
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentContacts = contacts.slice(startIndex, endIndex);

  // Debounced search function
  const debouncedSearch = useDebouncedCallback(async (query) => {
    if (!query.trim()) {
      loadAllContacts();
      return;
    }
    setIsSearching(true);
    setCurrentPage(1);
    try {
      const response = await getContactsViaQuickSearch(query);
      const contactsData = response.data || [];
      setContacts(contactsData);
      setTotalItems(contactsData.length);
    } catch (error) {
      setContacts([]);
      setTotalItems(0);
    } finally {
      setIsSearching(false);
    }
  }, 300);

  console.log(contacts);

  const loadAllContacts = async () => {
    setIsLoading(true);
    try {
      const response = await getContacts();
      const contactsData = response.data || [];
      setContacts(contactsData);
      setTotalItems(contactsData.length);
    } catch (error) {
      setContacts([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAllContacts();
  }, []);

  useEffect(() => {
    debouncedSearch(searchQuery);
  }, [searchQuery, debouncedSearch]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSort = (column) => {
    const isAsc = sortBy === column && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(column);
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    return phone.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
  };

  const getInitials = (firstname, lastname) => {
    return `${firstname?.charAt(0) || ''}${lastname?.charAt(0) || ''}`.toUpperCase();
  };
 

  const getStatusColor = (score) => {
    if (score >= 80) return '#FF0000'; // Red (hottest)
    if (score >= 60) return '#FF4500'; // Orange Red
    if (score >= 40) return '#FF8C00'; // Dark Orange
    if (score >= 20) return '#FFD700'; // Gold (cooler)
    return '#cdc6c6'; 
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          minHeight="50vh"
          gap={2}
        >
          <CircularProgress size={40} />
          <Typography variant="body1" color="text.secondary">
            Loading contacts...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header Toolbar */}
      <Paper elevation={0} sx={{ mb: 3, bgcolor: 'transparent' }}>
        <Toolbar sx={{ px: 0, minHeight: '64px !important' }}>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1, fontWeight: 600, color: '#1a1a1a' }}>
            Contacts
          </Typography>
          <Button
            component={Link}
            to="/contacts/add"
            variant="contained"
            startIcon={<Add />}
            sx={{ 
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' },
              borderRadius: 1,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Add Contact
          </Button>
        </Toolbar>
      </Paper>

      {/* Filters and Search Bar */}
      <Paper elevation={1} sx={{ mb: 3, bgcolor: '#fafafa', border: '1px solid #e0e0e0' }}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={handleSearchChange}
                slotProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                  endAdornment: isSearching && (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#d0d0d0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  sx={{ bgcolor: 'white' }}
                >
                  <MenuItem value="all">All Contacts</MenuItem>
                  <MenuItem value="buyer">Buyers</MenuItem>
                  <MenuItem value="seller">Sellers</MenuItem>
                  <MenuItem value="landlord">Landlords</MenuItem>
                  <MenuItem value="tenant">Tenants</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <FilterList fontSize="small" sx={{ color: '#666' }} />
                <Typography variant="body2" color="text.secondary">
                  {totalItems} contacts
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Contacts Table */}
      <Paper elevation={1} sx={{ border: '1px solid #e0e0e0' }}>
        {contacts.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <ContactPhone sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchQuery ? 'No contacts found' : 'No contacts yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchQuery 
                ? 'Try adjusting your search terms' 
                : 'Start by adding your first contact'
              }
            </Typography>
            {!searchQuery && (
              <Button
                component={Link}
                to="/contacts/add"
                variant="contained"
                startIcon={<Add />}
                sx={{ bgcolor: '#1976d2' }}
              >
                Add Contact
              </Button>
            )}
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 600, py: 2 }}>
                      <TableSortLabel
                        active={sortBy === 'name'}
                        direction={sortBy === 'name' ? sortOrder : 'asc'}
                        onClick={() => handleSort('name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }}>Contact Info</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }}>Score</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 600, width: 100 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentContacts.map((contact, index) => (
                    <TableRow 
                      key={contact.contactid}
                      hover
                      sx={{ 
                        '&:hover': { 
                          bgcolor: '#f8f9fa',
                          cursor: 'pointer'
                        },
                        '&:nth-of-type(even)': {
                          bgcolor: '#fafafa'
                        }
                      }}
                      onClick={() => navigate(`/contacts/${contact.contactid}/edit`)}
                    >
                      <TableCell sx={{ py: 1.5 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: getStatusColor(contact.contactScore),
                              fontSize: '14px',
                              fontWeight: 600
                            }}
                          >
                            {getInitials(contact.firstname, contact.lastname)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#1a1a1a' }}>
                              {contact.firstname} {contact.lastname}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>           
                      <TableCell sx={{ py: 1.5 }}>
                        {contact.contacttypename && (
                          <Chip 
                            label={contact.contacttypename}
                            size="small"
                            sx={{ 
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 500,
                              fontSize: '0.75rem'
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Stack spacing={0.5}>
                          {contact.email && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <EmailOutlined sx={{ fontSize: 14, color: '#666' }} />
                              <Typography variant="body2" sx={{ color: '#1976d2', fontSize: '0.8rem' }}>
                                {contact.email}
                              </Typography>
                            </Box>
                          )}
                          {(contact.phone || contact.mobile) && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <PhoneOutlined sx={{ fontSize: 14, color: '#666' }} />
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {formatPhone(contact.phone || contact.mobile)}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <OpScore score={contact.contactScore} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="View Contact">
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/contacts/${contact.contactid}/edit`);
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Contact">
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/contacts/${contact.contactid}/edit`);
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="More Actions">
                            <IconButton 
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems}
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default Contacts;